import * as AuthActions from './auth.actions';
import { CustomerModel } from '../../models/customer.model';
import { EmployeeModel } from "../../models/employee.model";
import { PaginationModel } from "../../models/pagination.model";

export interface State {
    employee: EmployeeModel;
    pagination: PaginationModel;
    customer: CustomerModel;
    email: string;
    jwt: any;
    inviteToken: string;
    loading: boolean;
    authChecked: boolean; // Track if auth has been checked
    // BankID states
    bankIdOrderRef?: string | null;
    bankIdQrStartToken?: string | null;
    bankIdQrStartSecret?: string | null;
    bankIdQrAutoStartToken?: string | null;
    bankIdError?: string | null;
    bankIdStatus?: string | null; // 'pending', 'outstandingTransaction', 'complete', 'failed'
    bankIdUserDetails?: {
        personalNumber: string;
        firstName: string;
        lastName: string;
        address?: string;
        city?: string;
        country?: string;
        email: string;
        phone?: string;
    } | null;
}

// Initial state for BankID-related properties should be explicitly null or undefined
// to ensure a clean slate.
const initialState: State = {
    employee: null,
    pagination: null,
    customer: null,
    email: null,
    jwt: null,
    inviteToken: null,
    loading: false,
    authChecked: false,
    // Initialize BankID states
    bankIdOrderRef: null,
    bankIdQrStartToken: null,
    bankIdQrStartSecret: null,
    bankIdQrAutoStartToken: null,
    bankIdError: null,
    bankIdStatus: null,
    bankIdUserDetails: null
}

export function authReducer(state = initialState, action: AuthActions.AuthActions): State {
    switch (action.type) {
        case AuthActions.UPDATE_BILLING_INFO:
            return {
                ...state,
                loading: true
            };

        case AuthActions.UPDATE_BILLING_INFO_SUCCESS:
            return {
                ...state,
                customer: state.customer ? {
                    ...state.customer,
                    billingInformation: action.payload.billing
                } : state.customer,
                loading: false
            };

        case AuthActions.UPDATE_BILLING_INFO_FAILED:
            return {
                ...state,
                loading: false
            };

        case AuthActions.CREATE_BILLING_INFO:
            return {
                ...state,
                loading: true
            };

        case AuthActions.CREATE_BILLING_INFO_SUCCESS:
            return {
                ...state,
                customer: state.customer ? {
                    ...state.customer,
                    billingInformation: action.payload.billing
                } : state.customer,
                loading: false
            };

        case AuthActions.CREATE_BILLING_INFO_FAILED:
            return {
                ...state,
                loading: false
            };

        case AuthActions.SIGNUP_START:
            return {
                ...state,
                email: action.payload.email,
                loading: true,
                authChecked: false
            };

        case AuthActions.SIGNUP_CODE_SENT:
            return {
                ...state,
                loading: true
            };

        case AuthActions.SIGNUP_CODE:
            return {
                ...state,
                loading: true
            };

        case AuthActions.LOGIN_LINK:
            return {
                ...state,
                loading: true,
                email: action.payload.email,
                authChecked: false
            };

        case AuthActions.LOGIN_LINK_SENT:
            return {
                ...state,
                loading: false
            };

        case AuthActions.LOGIN_START:
            return {
                ...state,
                loading: true,
                authChecked: false
            };

        case AuthActions.LOGIN_SUCCESS:
            let customerId_login = localStorage.getItem('customer');
            let customer_login = action.payload.employee?.customers?.find(x => x.id == +customerId_login);
            if (!customer_login && action.payload.employee?.customers?.length > 0) {
                customer_login = action.payload.employee.customers[0];
            }
            return {
                ...state,
                employee: action.payload.employee || state.employee,
                jwt: action.payload.jwt,
                customer: customer_login || state.customer,
                loading: false,
                authChecked: true,
            };

        case AuthActions.LOGIN_FAIL:
            return {
                ...state,
                loading: false,
                authChecked: true,
                employee: null,
                jwt: null,
                customer: null
            };

        case AuthActions.LOGOUT:
            localStorage.clear();
            return {
                ...initialState,
                authChecked: true,
                loading: false
            };

        case AuthActions.REGISTER_START:
            return {
                ...state,
                loading: true,
                authChecked: false
            };

        case AuthActions.REGISTER_SUCCESS:
            let customerID_reg = localStorage.getItem('customer');
            let cust_reg = action.payload.employee.customers?.find(x => x.id == +customerID_reg);
            if (!cust_reg && action.payload.employee.customers?.length > 0) {
                cust_reg = action.payload.employee.customers[0];
            }

            return {
                ...state,
                employee: action.payload.employee,
                jwt: action.payload.jwt,
                customer: cust_reg || state.customer,
                loading: false,
                authChecked: true,
            };

        case AuthActions.REGISTER_FAIL:
            return {
                ...state,
                loading: false,
                authChecked: true,
                employee: null,
                jwt: null,
                customer: null
            };

        case AuthActions.FORGOT_PASSWORD_START:
            return {
                ...state,
                loading: true
            };

        case AuthActions.FORGOT_PASSWORD_SUCCESS:
            return {
                ...state,
                loading: false
            };

        case AuthActions.FORGOT_PASSWORD_FAIL:
            return {
                ...state,
                loading: false
            };

        case AuthActions.RESET_PASSWORD_START:
            return {
                ...state,
                loading: true
            };

        case AuthActions.RESET_PASSWORD_SUCCESS:
            return {
                ...state,
                loading: false
            };

        case AuthActions.RESET_PASSWORD_FAIL:
            return {
                ...state,
                loading: false
            };

        case AuthActions.CHANGE_PASSWORD:
            return {
                ...state,
                loading: true
            };

        case AuthActions.CHANGE_PASSWORD_SUCCESS:
            return {
                ...state,
                employee: action.payload.employee,
                loading: false
            };

        case AuthActions.CHANGE_PASSWORD_FAILED:
            return {
                ...state,
                loading: false
            };

        case AuthActions.UPDATE_PROFILE:
            return {
                ...state,
                loading: true
            };

        case AuthActions.UPDATE_PROFILE_SUCCESS:
            return {
                ...state,
                employee: action.payload.employee,
                loading: false
            };

        case AuthActions.UPDATE_PROFILE_FAILED:
            return {
                ...state,
                loading: false
            };

        case AuthActions.VERIFY_NEW_EMAIL:
            return {
                ...state,
                loading: true
            };

        case AuthActions.VERIFY_NEW_EMAIL_SUCCESS:
            return {
                ...state,
                loading: false
            };

        case AuthActions.UPDATE_EMAIL:
            return {
                ...state,
                loading: true
            };

        // Customer Actions
        case AuthActions.CREATE_CUSTOMER:
            return {
                ...state,
                loading: true
            };

        case AuthActions.CREATE_CUSTOMER_SUCCESS:
            const updatedCustomersCreate = state.employee?.customers ?
                [...state.employee.customers, action.payload.customer] :
                [action.payload.customer];

            return {
                ...state,
                customer: action.payload.customer,
                employee: state.employee ? {
                    ...state.employee,
                    customers: updatedCustomersCreate
                } : state.employee,
                loading: false
            };

        case AuthActions.CREATE_CUSTOMER_FAILED:
            return {
                ...state,
                loading: false
            };

        case AuthActions.UPDATE_CUSTOMER:
            return {
                ...state,
                loading: true
            };

        case AuthActions.UPDATE_CUSTOMER_SUCCESS:
            const updatedCustomersUpdate = state.employee?.customers ?
                state.employee.customers.map(c => c.id === action.payload.customer.id ? action.payload.customer : c) :
                [];

            return {
                ...state,
                customer: action.payload.customer,
                employee: state.employee ? {
                    ...state.employee,
                    customers: updatedCustomersUpdate
                } : state.employee,
                loading: false
            };

        case AuthActions.UPDATE_CUSTOMER_FAILED:
            return {
                ...state,
                loading: false
            };

        case AuthActions.UPDATE_CUSTOMER_ORGANISATION_NUMBER_SUCCESS:
            const updatedCustomersOrg = state.employee?.customers
                ? state.employee.customers.map(c => c.id === action.payload.customer.id ? action.payload.customer : c)
                : [];
            return {
                ...state,
                customer: action.payload.customer,
                employee: state.employee ? {
                    ...state.employee,
                    customers: updatedCustomersOrg
                } : state.employee
            };

        case AuthActions.UPDATE_CUSTOMER_ORGANISATION_NUMBER_FAILED:
            return state;

        case AuthActions.SWITCH_COMPANY_ACCOUNT:
            return {
                ...state,
                customer: action.payload.customer
            };

        // Invite Token Actions
        case AuthActions.SET_INVITE_TOKEN:
            return {
                ...state,
                inviteToken: action.payload.inviteToken
            };

        case AuthActions.CLEAR_INVITE_TOKEN:
            return {
                ...state,
                inviteToken: null
            };

        // Transfer Ownership
        case AuthActions.TRANSFER_OWNERSHIP:
            return {
                ...state,
                employee: state.employee ? {
                    ...state.employee,
                    employeeRole: {
                        ...state.employee.employeeRole,
                        id: 2,
                        name: 'Administrator'
                    }
                } : state.employee
            };
        // BankID Reducer Cases
        case AuthActions.BANKID_AUTH_START:
            return {
                ...state,
                loading: true,
                bankIdError: null,
                bankIdOrderRef: null,
                bankIdQrStartToken: null,
                bankIdQrStartSecret: null,
                bankIdQrAutoStartToken: null,
                bankIdStatus: 'pending',
                bankIdUserDetails: null
            };

        case AuthActions.BANKID_AUTH_SUCCESS:
            return {
                ...state,
                loading: false,
                bankIdOrderRef: action.payload.orderRef,
                bankIdQrStartToken: action.payload.qrStartToken,
                bankIdQrStartSecret: action.payload.qrStartSecret,
                bankIdQrAutoStartToken: action.payload.qrAutoStartToken,
                bankIdError: null,
                bankIdStatus: 'outstandingTransaction'
            };

        case AuthActions.BANKID_AUTH_FAIL:
            return {
                ...state,
                loading: false,
                bankIdError: action.payload,
                bankIdOrderRef: null,
                bankIdQrStartToken: null,
                bankIdQrStartSecret: null,
                bankIdQrAutoStartToken: null,
                bankIdStatus: 'failed',
                bankIdUserDetails: null
            };

        case AuthActions.BANKID_COLLECT_PROGRESS:
            return {
                ...state,
                loading: false,
                bankIdStatus: action.payload,
                bankIdError: null
            };

        case AuthActions.BANKID_COLLECT_COMPLETE:
            return {
                ...state,
                loading: false,
                bankIdStatus: action.payload.status,
                bankIdUserDetails: action.payload.userDetails,
                bankIdError: null
            };

        case AuthActions.BANKID_COLLECT_FAIL:
            return {
                ...state,
                loading: false,
                bankIdError: action.payload,
                bankIdStatus: 'failed',
                bankIdUserDetails: null
            };

        case AuthActions.BANKID_AUTH_CLEAR:
            return {
                ...state,
                bankIdOrderRef: null,
                bankIdQrStartToken: null,
                bankIdQrStartSecret: null,
                bankIdQrAutoStartToken: null,
                bankIdError: null,
                bankIdStatus: null,
                bankIdUserDetails: null,
                loading: false
            };

        default:
            return state;
    }
}
